import type { Study, StudyOverviewInformationV1 } from '@/model/Study.model';
import { typeToTextMap } from '@/referenceData/study/type';
import type { ApiResponse } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';

import { type MetadataSection, useMetadataSections } from '../useMetadataSections';
import type { InformationCardData } from './InformationCard';

interface OverviewV2Data {
  information: InformationCardData;
  informationLoading: boolean;
  metadataSections: Array<MetadataSection>;
  visibleMetadataCount: number;
}

const UNRESOLVED_INFORMATION = {
  author: null,
  owner: null,
  createdAt: null,
  type: null,
  description: null,
} as const;

const EMPTY_INFORMATION: InformationCardData = {
  ...UNRESOLVED_INFORMATION,
  plannedAnimals: 0,
  livingAnimals: 0,
  deceasedAnimals: 0,
  groups: 0,
};

const FAILED_INFORMATION: InformationCardData = {
  ...UNRESOLVED_INFORMATION,
  plannedAnimals: null,
  livingAnimals: null,
  deceasedAnimals: null,
  groups: null,
};

const mapInformation = (body: StudyOverviewInformationV1): InformationCardData => ({
  author: body.author,
  owner: body.owner ?? body.author,
  createdAt: body.created_at,
  type: typeToTextMap[body.type] ?? body.type,
  description: body.description,
  plannedAnimals: body.planned_animals ?? 0,
  livingAnimals: body.living_animals ?? 0,
  deceasedAnimals: body.deceased_animals ?? 0,
  groups: body.groups ?? 0,
});

type OverviewInformationResponse = ApiResponse<'GET /api/v1/studies/{studyApiId}/overview/information'>;

const resolveInformation = (response?: OverviewInformationResponse): InformationCardData => {
  if (response?.type === 'success') {
    return mapInformation(response.body);
  }

  if (response?.type === 'error') {
    return FAILED_INFORMATION;
  }

  return EMPTY_INFORMATION;
};

export const useOverviewV2Data = (study: Study): OverviewV2Data => {
  const { loading: informationLoading, response: overviewInformationResponse } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/overview/information',
    path: { studyApiId: study.api_id },
    options: {
      invokeOnInit: true,
      onError: { toast: false, throw: false, capture: true, slug: 'study-overview-information-load' },
    },
  });

  const { metadataSections, visibleMetadataCount } = useMetadataSections({
    studyMetadata: study.metadata,
    formTemplateId: study.study_creation_template_id,
  });

  return {
    information: resolveInformation(overviewInformationResponse),
    informationLoading,
    metadataSections,
    visibleMetadataCount,
  };
};
